import { dispatchWarningToast } from 'packages/lib/common-ui/toast/api-toast';
import { z } from 'zod';

const attachmentTypeZod = z.enum([
  'Map',
  'Instruction',
  'Taxonomy',
  'ExampleImage',
  'Extraction',
  'Sensor',
  'Log',
  'LidarFrame',
  'Annotation',
  'VideoExtractionMetadata',
]);
type AttachmentType = z.infer<typeof attachmentTypeZod>;

const actionTypeZod = z.enum(['Created', 'Modified', 'Deleted', 'Unknown']);
type ActionType = z.infer<typeof actionTypeZod>;

const annotationTypeZod = z.enum(['BoundingBox', 'Polygon', 'Mask']);
type AnnotationType = z.infer<typeof annotationTypeZod>;

const annotationPartnerZod = z.enum([
  'None',
  'Scale',
  'CloudFactory',
  'NuTonomy',
  'Samasource',
  'MightyAI',
  'Annotell',
]);
type AnnotationPartner = z.infer<typeof annotationPartnerZod>;

const detectionErrorTypeZod = z.enum([
  'FalsePositive',
  'FalseNegtive',
  'LabelMismatch',
]);
type DetectionErrorType = z.infer<typeof detectionErrorTypeZod>;

const regionShapeZod = z.enum(['Box', 'Polygon']);
type RegionShape = z.infer<typeof regionShapeZod>;

const binaryFormatZod = z.enum(['Unknown']);
type BinaryFormat = z.infer<typeof binaryFormatZod>;

const polygonTypeZod = z.enum(['Instance', 'Hole']);
type PolygonType = z.infer<typeof polygonTypeZod>;

const jobTypeZod = z.enum(['GroundTruthImport']);
type JobType = z.infer<typeof jobTypeZod>;

const jobStateZod = z.enum([
  'Created',
  'Ready',
  'Assigned',
  'Running',
  'Done',
  'Cancelled',
]);
type JobState = z.infer<typeof jobStateZod>;

const jobStatusZod = z.enum(['Pending', 'Succeeded', 'Failed']);
type JobStatus = z.infer<typeof jobStatusZod>;

const vehicleTypeZod = z.enum([
  'Zoe',
  'D3Devkit',
  'MeltingPot',
  'Gen1',
  'Gen2',
  'Gen3',
  'Gen3Mule',
]);
type VehicleType = z.infer<typeof vehicleTypeZod>;

export function isVehicleType(type: VehicleType): boolean {
  return vehicleTypeZod.safeParse(type).success;
}

export interface VehicleOption {
  displayName: VehicleType;
  key:
    | 'zoe'
    | 'd3devkit'
    | 'meltingpot'
    | 'gen1'
    | 'gen2'
    | 'gen3'
    | 'gen3mule';
}

export const vehicleFilterOptions: VehicleOption[] = [
  {
    displayName: 'D3Devkit',
    key: 'd3devkit',
  },
  {
    displayName: 'MeltingPot',
    key: 'meltingpot',
  },
  {
    displayName: 'Zoe',
    key: 'zoe',
  },
  {
    displayName: 'Gen1',
    key: 'gen1',
  },
  {
    displayName: 'Gen2',
    key: 'gen2',
  },
  {
    displayName: 'Gen3',
    key: 'gen3',
  },
  {
    displayName: 'Gen3Mule',
    key: 'gen3mule',
  },
];

const logTypeZod = z.enum([
  'Unknown',
  'Dev',
  'Mapping',
  'Simulation',
  'RoadRelease',
]);
type LogType = z.infer<typeof logTypeZod>;

export function isLogType(type: LogType): boolean {
  return logTypeZod.safeParse(type).success;
}

const batchTypeZod = z.enum(['Image', 'Lidar', 'Video', 'Audio']);
type BatchType = z.infer<typeof batchTypeZod>;

// IMPORTANT: When adding a new type here, also add it in components/sample-groups/sample-group.tsx > renderDataView()
const mediaTypeZod = z.enum(['Image', 'Lidar', 'Video', 'Audio']);
type MediaType = z.infer<typeof mediaTypeZod>;

const batchStageZod = z.enum([
  'Creating',
  'Initial',
  'First',
  'Second',
  'Third',
  'Final',
]);
type BatchStage = z.infer<typeof batchStageZod>;

const sensorTypeZod = z.enum([
  'Lidar',
  'Camera',
  'Radar',
  'Microphone',
  'Pose',
]);
type SensorType = z.infer<typeof sensorTypeZod>;

// Per Edvin: For LidarTopDown, the Pipeline is called BirdsEyeView.
// The Task Group Type / Task Type is callen LidarTopDown.
// There is no 'BEV' in the backend
const taskTypes = [
  'Attribute',
  'Audio',
  'DefectTracking',
  'FullScene',
  'Fusion',
  'GenericObject',
  'GtRegression',
  'ImageBox',
  'LidarBox',
  'LidarBoxEnrichment',
  'LidarBoxAmplification',
  'LidarSeg',
  'Linking',
  'LinkingMask',
  'LidarTopDown',
  'Image',
  'ImageEnrichment',
  'InstanceSeg',
  'PanopticSeg',
  'PartialImage',
  'PolygonGrouping',
  'RareObjects',
  'SceneEnrichment',
  'SemSeg',
  'TrackletClassification',
  'TrafficLight',
  'VideoEvent',
] as const;

const taskTypeZod = z.enum(taskTypes);
type TaskType = z.infer<typeof taskTypeZod>;

export const taskTypeZodWithCatch = taskTypeZod.catch(({ error, input }) => {
  if (typeof input === 'string') {
    dispatchWarningToast(
      `"${input}" is not a registered TaskType, but the error has been automatically handled. Please report this issue to the Annotation Tools team.`,
      false
    );
    return input as (typeof taskTypes)[number];
  }
  throw error;
});

const taskSampleTypeZod = z.enum([
  'Sample',
  'SampleData',
  'ImageAnnotation',
  'LidarAnnotation',
  'SampleGroup',
  'LensAnnotation',
]);
type TaskSampleType = z.infer<typeof taskSampleTypeZod>;

const taskStateZod = z.enum([
  'ToDo',
  'InProgress',
  'InReview',
  'Done',
  'Cancelled',
  'Error',
  'Merged',
  'Rejected',
]);
type TaskState = z.infer<typeof taskStateZod>;

const annotationStates = [
  'ToDo',
  'AnnotationInProgress',
  'AnnotationInReview',
  'QaToDo',
  'QaInProgress',
  'QaDone',
  'AuditToDo',
  'AuditInProgress',
  'AuditDone',
  'Reopened',
  'Flagged',
  'FlaggedInReview',
  'FlaggedDone',
  'Cancelled',
  'Error',
  'ErrorInReview',
  // NOTE: There is no 'ErrorDone' AnnotationState in the backend
] as const;

const annotationStateZod = z.enum(annotationStates);
type AnnotationState = z.infer<typeof annotationStateZod>;

const taskPriorityZod = z.enum([
  'Lowest',
  'Low',
  'Medium',
  'High',
  'Highest',
  'Hidden',
]);
type TaskPriority = z.infer<typeof taskPriorityZod>;

export const taskGroupTypes = [
  'LidarBox',
  'GroundTruth',
  'ImageBox',
  'Linking',
  'SemSeg',
  'LidarSeg',
  'InstanceSeg',
  'VideoEvent',
  'TrafficLight',
  'PolygonGrouping',
  'LinkingMask',
  'PanopticSeg',
  'Audio',
  'LidarTopDown',
  'ImageEnrichment',
  'SceneEnrichment',
  'LidarBoxEnrichment',
  'LidarBoxAmplification',
] as const;

const taskGroupTypeZod = z.enum(taskGroupTypes);
type TaskGroupType = z.infer<typeof taskGroupTypeZod>;

const taskGroupStateZod = z.enum([
  'ToDo',
  'InReview',
  'Accepted',
  'Rejected',
  'Cancelled',
  'InProgress',
]);
type TaskGroupState = z.infer<typeof taskGroupStateZod>;

const pipelines = [
  'Image',
  'Fusion',
  'GtRegression',
  'FusionCloudFactory',
  'NuScenes',
  'Baselabs',
  'VideoEvent',
  'TrafficLight',
  'DefectTracking',
  'GenericObject',
  'FullScene',
  'RareObjects',
  'Audio',
  'BirdsEyeView', // Will contain LidarTopDown workflows
  'VehicleLight',
  'PartialImage',
  'ActiveTrafficParticipant',
  'AutoImage',
  'AutoLidar',
  'Enrichment',
  'LidarEnrichment',
] as const;

export function pipelineToBatchType(
  pipelineName: Pipeline | string
): BatchType {
  switch (pipelineName) {
    case 'Image':
    case 'PartialImage':
    case 'AutoImage':
    case 'Enrichment':
    case 'TrafficLight':
    case 'VehicleLight':
      return 'Image';
    case 'Audio':
      return 'Audio';
    case 'VideoEvent':
      return 'Video';
    case 'FullScene':
    case 'GenericObject':
    case 'GtRegression':
    case 'DefectTracking':
    case 'RareObjects':
    case 'BirdsEyeView':
    case 'AutoLidar':
    case 'LidarEnrichment':
      return 'Lidar';
    default:
      return 'Lidar';
  }
}

const pipelineZod = z.enum(pipelines);
type Pipeline = z.infer<typeof pipelineZod>;

export const pipelineZodWithCatch = pipelineZod.catch(({ error, input }) => {
  if (typeof input === 'string') {
    dispatchWarningToast(
      `"${input}" is not a registered Pipeline, but the error has been automatically handled. Please report this issue to the Annotation Tools team.`,
      false
    );
    return input as (typeof pipelines)[number];
  }
  throw error;
});

const eventTypeZod = z.enum(['Point', 'Range']);
type EventType = z.infer<typeof eventTypeZod>;

const lensStateZod = z.enum([
  'Off',
  'On',
  'Blinking',
  'NotVisible',
  'Unusable',
]);
type LensState = z.infer<typeof lensStateZod>;

const lidarTopDownResponseTypeZod = z.enum([
  'World',
  'Ortho',
  'Camera',
  'Image',
]);
type LidarTopDownResponseType = z.infer<typeof lidarTopDownResponseTypeZod>;

export {
  attachmentTypeZod,
  actionTypeZod,
  annotationStates,
  annotationTypeZod,
  annotationPartnerZod,
  detectionErrorTypeZod,
  regionShapeZod,
  binaryFormatZod,
  polygonTypeZod,
  jobTypeZod,
  jobStateZod,
  jobStatusZod,
  vehicleTypeZod,
  logTypeZod,
  batchTypeZod,
  mediaTypeZod,
  batchStageZod,
  sensorTypeZod,
  taskTypeZod,
  taskSampleTypeZod,
  taskStateZod,
  annotationStateZod,
  taskPriorityZod,
  taskGroupTypeZod,
  taskGroupStateZod,
  pipelineZod,
  eventTypeZod,
  lensStateZod,
  lidarTopDownResponseTypeZod,
};

export type {
  AttachmentType,
  ActionType,
  AnnotationType,
  AnnotationPartner,
  DetectionErrorType,
  RegionShape,
  BinaryFormat,
  PolygonType,
  JobType,
  JobState,
  JobStatus,
  VehicleType,
  LogType,
  BatchType,
  MediaType,
  BatchStage,
  SensorType,
  TaskType,
  TaskSampleType,
  TaskState,
  AnnotationState,
  TaskPriority,
  TaskGroupType,
  TaskGroupState,
  Pipeline,
  EventType,
  LensState,
  LidarTopDownResponseType,
};
